import React, { useEffect, useRef, useState } from "react";
import { getCashPhoto } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { PRINT_GEOMETRY } from "../print/CashSheetPrint";

/**
 * Cash book photos are private (V1_SPEC D29): no media-service copies exist, the upload itself is the
 * ≤ 2000 px copy, and the server hands out a 15-minute signed link to owners, managers and the uploader
 * only. So these images use the signed link directly (lazy, async decode) instead of HKPImage, and the
 * link is kept in memory for a little less than its life so a strip of row crops asks once per photo.
 */
type Cached = { url: string; until: number };
const cache = new Map<string, Cached>();
const inFlight = new Map<string, Promise<string | null>>();
const LINK_MS = 12 * 60_000;

export const forgetPhotoLinks = () => {
  cache.clear();
  inFlight.clear();
};

export const photoLink = (photoId: string): Promise<string | null> => {
  const hit = cache.get(photoId);
  if (hit && hit.until > Date.now()) return Promise.resolve(hit.url);
  const running = inFlight.get(photoId);
  if (running) return running;
  const p = getCashPhoto(photoId)
    .then((res) => {
      const url = res?.success ? res.data?.readUrl : null;
      if (url) cache.set(photoId, { url, until: Date.now() + LINK_MS });
      return url || null;
    })
    .catch(() => null)
    .finally(() => inFlight.delete(photoId));
  inFlight.set(photoId, p);
  return p;
};

export const usePhotoLink = (photoId: string | null | undefined): { url: string | null; failed: boolean; retry: () => void } => {
  const [state, setState] = useState<{ url: string | null; failed: boolean }>({ url: null, failed: false });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ url: null, failed: false });
    if (!photoId) return;
    photoLink(photoId).then((url) => live && setState({ url, failed: !url }));
    return () => {
      live = false;
    };
  }, [photoId, tick]);
  return { ...state, retry: () => setTick((n) => n + 1) };
};

type Band = { center: number; height: number };

/**
 * Where to place the page image inside a crop box so the box shows `visible` of the page height centred
 * on the band, and the middle `width` of the page across (both fractions). The box has the aspect
 * A4 × width / visible, so the image is (1 / width) box widths wide and (1 / visible) box heights tall.
 */
export const cropStyle = (band: Band, visible: number, width = 0.92): { box: React.CSSProperties; img: React.CSSProperties } => {
  const v = Math.min(1, Math.max(0.01, visible));
  const w = Math.min(1, Math.max(0.1, width));
  const top = Math.min(Math.max(band.center - v / 2, 0), 1 - v);
  const left = (1 - w) / 2;
  const pct = (x: number) => `${Math.round(x * 10000) / 100}%`;
  return {
    box: { aspectRatio: `${(PAGE_ASPECT * w) / v}` },
    img: { position: "absolute", width: pct(1 / w), height: "auto", left: pct(-left / w), top: pct(-top / v), maxWidth: "none" },
  };
};

const PAGE_ASPECT = PRINT_GEOMETRY.pageWidthMm / PRINT_GEOMETRY.pageHeightMm;

type CropProps = { photoId: string | null; band: Band; label: string; rows?: number };

/** A strip crop of one row band (group verify, the worker card): about three rows tall. */
export const RowCrop = ({ photoId, band, label, rows = 3 }: CropProps) => {
  const { t } = useKT();
  const { url, failed, retry } = usePhotoLink(photoId);
  const { box, img } = cropStyle(band, band.height * rows);
  if (!photoId) return <div className="cbo-crop is-empty">{t("No photo")}</div>;
  return (
    <div className="cbo-crop" style={box}>
      {url ? (
        <img src={url} alt={label} loading="lazy" decoding="async" style={img} />
      ) : failed ? (
        <button type="button" className="cb-link" onClick={retry}>
          {t("Photo didn't load. Retry")}
        </button>
      ) : (
        <div className="cb-skeleton" aria-label={t("Loading…")} />
      )}
      <span className="cbo-crop__mark" aria-hidden="true" />
    </div>
  );
};

type PanelProps = {
  photoId: string | null;
  /** Row band to pan to and mark; null shows the page from the top with no mark. */
  band: Band | null;
  alt: string;
  /** Text under the photo when there is none for this page. */
  emptyText?: string;
};

/**
 * The page photo beside the rows: zoomable, scrolled so the current row's band sits in the middle,
 * with the band marked (D31: an approximate pan from the print geometry, no server crop).
 */
const PhotoBand = ({ photoId, band, alt, emptyText }: PanelProps) => {
  const { t } = useKT();
  const { url, failed, retry } = usePhotoLink(photoId);
  const [zoom, setZoom] = useState(1.3);
  const scroller = useRef<HTMLDivElement>(null);
  const img = useRef<HTMLImageElement>(null);

  const pan = () => {
    const box = scroller.current;
    const pic = img.current;
    if (!box || !pic || !band) return;
    const h = pic.getBoundingClientRect().height;
    const w = pic.getBoundingClientRect().width;
    const top = band.center * h - box.clientHeight / 2;
    const left = (w - box.clientWidth) / 2;
    if (typeof box.scrollTo === "function") box.scrollTo({ top: Math.max(0, top), left: Math.max(0, left), behavior: "smooth" });
  };

  useEffect(pan, [band?.center, zoom, url]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!photoId) return <div className="cbo-photo is-empty">{emptyText || t("No photo for this page yet")}</div>;
  return (
    <div className="cbo-photo">
      <div className="cbo-photo__scroll" ref={scroller}>
        {url ? (
          <div className="cbo-photo__page" style={{ width: `${zoom * 100}%` }}>
            <img ref={img} src={url} alt={alt} decoding="async" onLoad={pan} />
            {band ? <span className="cbo-photo__band" style={{ top: `${(band.center - band.height / 2) * 100}%`, height: `${band.height * 100}%` }} aria-hidden="true" /> : null}
          </div>
        ) : failed ? (
          <div className="cbo-photo__msg">
            {t("Photo didn't load.")}{" "}
            <button type="button" className="cb-link" onClick={retry}>
              {t("Retry")}
            </button>
          </div>
        ) : (
          <div className="cb-skeleton is-photo" role="status" aria-label={t("Loading…")} />
        )}
      </div>
      <div className="cbo-photo__zoom">
        <button type="button" className="kh-btn" onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.4) * 10) / 10))} aria-label={t("Zoom out")} disabled={zoom <= 1}>
          −
        </button>
        <button type="button" className="kh-btn" onClick={() => setZoom((z) => Math.min(3.2, Math.round((z + 0.4) * 10) / 10))} aria-label={t("Zoom in")} disabled={zoom >= 3.2}>
          +
        </button>
      </div>
    </div>
  );
};

export default PhotoBand;
